import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import confirmDialog from '../../components/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { formatTimestamp } from '../../utils/dateUtils';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser, hasPermission } = useAuth();
  const { t } = useTranslation(['users', 'common']);
  
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await userApi.getAll();
        setUsers(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(t('users:loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, []);
  
  const handleDeleteUser = async (userId, username) => {
    // Use the custom confirm dialog
    confirmDialog({
      message: t('users:confirmDelete', { username }),
      onConfirm: async () => {
        try {
          await userApi.delete(userId);
          
          // Remove user from state
          setUsers(users.filter(user => user.id !== userId));
          
          // Show success message using toast
          toast.success(t('users:deleteSuccess', { username }));
        } catch (err) {
          console.error('Error deleting user:', err);
          toast.error(err.response?.data?.detail || t('users:deleteFailed'));
        }
      }
    });
  };
  
  // Get role display name
  const getRoleDisplayName = (role) => {
    const roleKey = `users:roles.${role}`;
    return t(roleKey, { defaultValue: role });
  };
  
  // Get role badge class
  const getRoleBadgeClass = (role) => {
    const classMap = {
      'owner': 'bg-danger',
      'admin': 'bg-primary',
      'member_manager': 'bg-success',
      'viewer': 'bg-secondary'
    };
    
    return classMap[role] || 'bg-secondary';
  };
  
  if (!hasPermission('users', 'view')) {
    return (
      <div className="alert alert-danger" role="alert">
        You don't have permission to view users.
      </div>
    );
  }
  
  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{t('users:title')}</h1>
        
        {hasPermission('users', 'create') && (
          <Link to="/users/new" className="btn btn-primary">
            <i className="fas fa-plus me-2"></i>
            {t('users:addUser')}
          </Link>
        )}
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="card shadow">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>{t('users:fields.username')}</th>
                    <th>{t('users:fields.email')}</th>
                    <th>{t('users:fields.role')}</th>
                    <th>{t('users:fields.status')}</th>
                    <th>{t('users:fields.services')}</th>
                    <th>{t('users:fields.lastLogin')}</th>
                    <th>{t('common:actions.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        {t('users:noUsers')}
                      </td>
                    </tr>
                  ) : (
                    users.map(user => (
                      <tr key={user.id}>
                        <td>{user.username}</td>
                        <td>{user.email || '-'}</td>
                        <td>
                          <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                            {getRoleDisplayName(user.role)}
                          </span>
                        </td>
                        <td>
                          {user.is_active ? (
                            <span className="badge bg-success">{t('users:status.active')}</span>
                          ) : (
                            <span className="badge bg-danger">{t('users:status.inactive')}</span>
                          )}
                        </td>
                        <td>
                          {user.service_ids && user.service_ids.length > 0 ? (
                            <span className="badge bg-info">{user.service_ids.length} services</span>
                          ) : (
                            <span className="text-muted">{t('common:messages.noData')}</span>
                          )}
                        </td>
                        <td>
                          {user.last_login ? formatTimestamp(user.last_login) : t('users:never')}
                        </td>
                        <td>
                          {hasPermission('users', 'update') && (
                            <Link 
                              to={`/users/${user.id}/edit`} 
                              className="btn btn-sm btn-outline-primary me-2"
                              title={t('users:actions.edit')}
                            >
                              <i className="fas fa-edit"></i>
                            </Link>
                          )}
                          
                          {hasPermission('users', 'delete') && user.id !== currentUser.id && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteUser(user.id, user.username)}
                              title={t('users:actions.delete')}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
